import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Check } from "lucide-react";
import { IMAGES, STATS, WHY } from "../mock";

function ServiceIcon({ name, className }) {
  const Cmp = Icons[name] || Icons.Building2;
  return <Cmp className={className} />;
}
function Reveal({ children, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && el.classList.add("in-view"),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`fade-up ${className}`}>{children}</div>;
}

const VALUES = [
  { title: "Sustainability", text: "Eco-conscious innovation and energy-efficient tech at the core of every build." },
  { title: "Affordability", text: "Obliterating financial barriers so young people can own without inherited wealth." },
  { title: "Community", text: "We don't just build houses; we create thriving communities that pulse with life." },
];

export default function About() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1F3463] pb-20 pt-48">
        <img src={IMAGES.community} alt="Community" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F3463] to-[#1F3463]/60" />
        <div className="relative mx-auto max-w-7xl px-5 text-center lg:px-8">
          <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">
            Who We Are
          </span>
          <h1 className="mx-auto mt-3 max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl">
            A family-run company with a clear purpose
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-white/75">
            Helping young people get into their first home, whatever their
            background and without relying on inherited wealth.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal className="relative">
              <div className="img-zoom overflow-hidden rounded-2xl shadow-2xl">
                <img src={IMAGES.coupleKeys} alt="New homeowners" className="h-[480px] w-full object-cover" />
              </div>
              <div className="absolute -right-6 -top-6 hidden rounded-xl bg-[#C9A876] px-8 py-6 text-center shadow-xl sm:block">
                <div className="font-display text-3xl font-bold text-[#15264a]">100%</div>
                <div className="text-xs font-semibold uppercase tracking-widest text-[#15264a]/80">Eco focus</div>
              </div>
            </Reveal>
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">Our Story</span>
              <h2 className="mt-3 font-display text-3xl font-bold text-[#1F3463] sm:text-4xl">
                Making home ownership a reality
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-slate-600">
                Our aim is simple: to make home ownership achievable for young
                people, with affordable, sustainable, good-quality homes designed
                around their needs.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                We wish to work with trusted investors to support a housing
                solution that helps young people build commitment, build
                communities, and get on the housing ladder. We build and maintain
                welcoming, eco-conscious communities, and we are on track to be the
                trusted housebuilder for a generation to come.
              </p>
              <Link to="/services" className="btn-navy mt-8 inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold uppercase tracking-wide">
                What we do <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F7F8FA] py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">Our Values</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-[#1F3463] sm:text-4xl">What drives us forward</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {VALUES.map((v) => (
              <Reveal key={v.title}>
                <div className="card-lift h-full rounded-2xl bg-white p-8 shadow-sm">
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#1F3463] text-[#C9A876]">
                    <Check className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[#1F3463]">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="brand-texture bg-[#1F3463] py-16">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-5 lg:grid-cols-4 lg:px-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-4xl font-bold text-[#C9A876]">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-white/60">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HERITAGE */}
      <section className="py-24">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-widest text-[#C9A876]">Our Heritage</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-[#1F3463] sm:text-4xl">
              Three decades in the making
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              Our family has worked in property for more than three decades, and
              that experience shapes everything we do today. From building bespoke
              homes to leading large-scale regeneration projects — including work
              on a Premier League football stadium — our founders have learned
              every part of property development first-hand.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              That hard-earned expertise helps us deliver strong results, working
              closely with clients, partners and investors on every project.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              With over 30 years of experience, our team has covered everything
              from crafting bespoke family homes to leading large 900-home
              Build-to-Rent schemes — complete with professional football
              stadiums, retail spaces and a range of housing tenures.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY STRIP */}
      <section className="bg-[#F7F8FA] py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w) => (
              <Reveal key={w.title}>
                <div className="flex h-full flex-col rounded-2xl bg-white p-7 shadow-sm">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-[#F5F1E8] text-[#1F3463]">
                    <ServiceIcon name={w.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#1F3463]">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
